// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Logic --------------------------------------------------------------------------------------
export type TySelection = {
    /** What is picked, in the order it was picked. */
    ids: string[];
    count: number;
    /** Whether the bar is up — which is the same question as "has anything been picked". */
    isSelecting: boolean;
    has: (id: string) => boolean;
    /** Picks or unpicks one. Entering the mode and adding to it are the same gesture. */
    toggle: (id: string) => void;
    clear: () => void;
};

/**
 * Which rows a list has picked, for the bulk bar above it.
 *
 * **Page-scoped on purpose, and not persisted anywhere.** Paging while twelve rows are selected would
 * leave a write pointing at records nobody can see any more, and "apply to the twelve I picked three
 * pages ago" is a promise a screen cannot keep — so the bar replaces the pager for as long as it is
 * up, and reloading, filtering or switching tab drops the selection with the list it was made from.
 *
 * There is **no select-all**, deliberately. A tick that reaches rows behind a filter somebody has
 * forgotten is how a hundred records get deleted by a person who meant four.
 */
export default function useSelection(): TySelection {
    const [ids, setIds] = useState<string[]>([]);

    const toggle = (id: string) => setIds((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));

    return {
        ids,
        count: ids.length,
        isSelecting: ids.length > 0,
        has: (id: string) => ids.includes(id),
        toggle,
        clear: () => setIds([]),
    };
}
